import { CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import KODIAK_FARM_ABI from 'abis/KodiakFarm.json'
import { KodiakFarmStake } from 'hooks/useKodiakFarm'
import { useWriteContract } from 'hooks/useTransaction'
import sumCurrencyAmount from 'lib/utils/sumCurrencyAmount'
import { useCallback, useState } from 'react'
import { useTransactionAdder } from 'state/transactions/hooks'
import { TransactionType } from 'state/transactions/types'
import { Address } from 'viem'

export interface UnstakePosition {
  kekId: string
  liquidity: CurrencyAmount<Token>
}

export function useOnUnstakeCallback(
  stakesToWithdraw: KodiakFarmStake[],
  farmAddress: Address | undefined,
  clear?: () => void
) {
  const { writeContractAsync } = useWriteContract()
  const [attemptingTxnUnstake, setAttemptingTxnUnstake] = useState(false)
  const [txHashUnstake, setTxHashUnstake] = useState<string>('')
  const addTransaction = useTransactionAdder()

  const onConfirmWithdrawStakes = useCallback(async () => {
    if (!stakesToWithdraw.length || !farmAddress) {
      return
    }

    setAttemptingTxnUnstake(true)

    const kek_ids = stakesToWithdraw.map((stake) => stake.kekId as Address)

    try {
      const hash = await writeContractAsync({
        address: farmAddress,
        abi: KODIAK_FARM_ABI,
        functionName: 'withdrawLockedMultiple',
        args: [kek_ids],
      })

      setTxHashUnstake(hash)
      const sum = sumCurrencyAmount(stakesToWithdraw.map((item) => item.liquidity))

      addTransaction(
        { hash },
        {
          type: TransactionType.UNSTAKE_LIQUIDITY_ISLAND,
          amount: `${sum?.toSignificant(6) || 0} ${sum?.currency?.symbol}`,
        }
      )
    } catch (error) {
      console.error('Failed to unstake positions:', error)
      throw error
    } finally {
      setAttemptingTxnUnstake(false)
    }
  }, [stakesToWithdraw, farmAddress, addTransaction, writeContractAsync])

  return {
    onConfirmWithdrawStakes,
    attemptingTxn: attemptingTxnUnstake,
    pendingText: `Unstaking ${stakesToWithdraw.length} position${stakesToWithdraw.length !== 1 ? 's' : ''}`,
    hash: txHashUnstake,
    clear: () => {
      setAttemptingTxnUnstake(false)
      setTxHashUnstake('')
      clear && clear()
    },
  }
}
